import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Monitor, ClipboardList, BadgeDollarSign, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MTopBar } from "@/components/mobile/MTopBar";
import { MPage, SearchBox, cardCls } from "@/components/mobile/parts";
import { HomeFavorites } from "@/components/mobile/HomeFavorites";
import { ComandaCard } from "@/components/mobile/ComandaCard";
import { useOpenComandas } from "@/hooks/useMobileData";
import { api, brl } from "@/lib/api";
import { canSales, todaySales, filterComandas, norm, screensFor, isDirect, MAIN_SCREENS, CONFIG_SCREENS } from "@/lib/mobile";
import { cn } from "@/lib/utils";

function BigCard({ to, icon: Icon, title, hint, wide, primary, testId }) {
  return (
    <Link to={to} data-testid={testId} className={cn("relative flex min-h-[136px] flex-col justify-between overflow-hidden rounded-3xl p-5 transition-transform active:scale-[0.98]", wide && "col-span-2",
      primary ? "bg-[#1565c0] text-white shadow-lg shadow-blue-900/20" : "bg-white shadow-sm ring-1 ring-slate-200")}>
      {primary && <span className="absolute -bottom-12 -right-10 h-40 w-40 rounded-full bg-white/10" />}
      <span className={cn("grid h-12 w-12 place-items-center rounded-2xl", primary ? "bg-white/15" : "bg-[#e3f2fd] text-[#1565c0]")}><Icon className="h-6 w-6" /></span>
      <div className="relative">
        <p className="font-display text-2xl font-bold">{title}</p>
        <p className={cn("text-sm", primary ? "text-white/80" : "text-slate-500")} data-testid={`${testId}-hint`}>{hint}</p>
      </div>
      <ChevronRight className="absolute right-4 top-5 h-5 w-5 opacity-50" />
    </Link>
  );
}

function Results({ q, comandas, user, tenant }) {
  const nav = useNavigate();
  const cs = filterComandas(comandas, q).slice(0, 8);
  const screens = [...screensFor(user, MAIN_SCREENS, tenant), ...CONFIG_SCREENS].filter((s) => norm(s.label).includes(norm(q)));
  return (
    <div className="space-y-3" data-testid="m-home-results">
      {cs.map((c) => <ComandaCard key={c.id} c={c} showWaiter={!!user.waiter_view} onClick={() => nav(`/m/comanda/${c.id}`)} />)}
      {screens.map((s) => (
        <Link key={s.to} to={s.to} className={cn(cardCls, "flex items-center gap-3 p-3 text-sm font-semibold")} data-testid={`m-result-${s.to.split("/").pop() || "inicio"}`}>
          <s.icon className="h-5 w-5 text-[#1565c0]" />{s.label}<ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
        </Link>
      ))}
      {!cs.length && !screens.length && <p className="py-8 text-center text-sm text-slate-500">Nada encontrado para "{q}".</p>}
    </div>
  );
}

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite"; };

export default function MHome() {
  const { session } = useAuth();
  const u = session.user;
  const [q, setQ] = useState("");
  const [sales, setSales] = useState(null);
  const { list } = useOpenComandas(15000);
  const showSales = canSales(u);
  useEffect(() => { if (showSales) api.get("/sales?limit=300").then((r) => setSales(todaySales(r.data))).catch(() => {}); }, [showSales]);
  const open = list || [];
  const direct = isDirect(session.user, session.tenant);
  return (
    <>
      <MTopBar title="Início" />
      <MPage>
        <div className="fade-up">
          <p className="text-sm text-slate-500">{new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })}</p>
          <h1 className="font-display text-3xl font-bold tracking-tight" data-testid="m-greeting">{greet()}, {u.name.split(" ")[0]}!</h1>
        </div>
        <SearchBox value={q} onChange={setQ} placeholder="Buscar comanda, cliente ou tela" testId="m-home-search" />
        {q.trim() ? <Results q={q} comandas={direct ? [] : open} user={u} tenant={session.tenant} /> : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <BigCard to="/m/pdv" icon={Monitor} title="PDV" hint={direct ? "Nova venda direta" : list ? `${open.length} comanda(s) aberta(s)` : "Comandas abertas"} wide primary testId="m-home-pdv" />
              {!direct && <BigCard to="/m/comandas" icon={ClipboardList} title="Comandas" hint="Lista e busca" wide={!showSales} testId="m-home-comandas" />}
              {showSales && <BigCard to="/m/vendas" icon={BadgeDollarSign} title="Vendas" hint={sales ? `${brl(sales.total)} hoje` : "Vendas de hoje"} wide={direct} testId="m-home-vendas" />}
            </div>
            <HomeFavorites />
          </>
        )}
      </MPage>
    </>
  );
}
